import { AxiosError } from 'axios'
import { useQuery } from 'react-query'
import { QUERY_KEYS } from '@/lib/cache-keys/queryKeys'
import { affiliatesApi } from 'api/affiliatesApi'
import { IAffiliate } from 'types/affiliates'

// The account page keeps the balance and player count current while it's open.
const LIVE_REFRESH_INTERVAL_MS = 60 * 1000
const LIVE_STALE_TIME_MS = 30 * 1000
// Elsewhere (e.g. the drawer link) we only need to know whether the user is a partner.
const CACHED_STALE_TIME_MS = 5 * 60 * 1000

/**
 * The logged-in user's partner organisation.
 * Resolves to `null` when the user isn't a partner (404 AFFILIATE_NOT_FOUND).
 *
 * `isLive` refreshes in the background every minute and when the tab regains focus
 * (only while the tab is visible); otherwise the result is reused for 5 minutes.
 */
export const useAffiliateMe = (options?: {
  enabled?: boolean
  initialData?: IAffiliate
  isLive?: boolean
}) =>
  useQuery<IAffiliate | null>(
    [QUERY_KEYS.AFFILIATE_ME],
    async () => {
      try {
        return await affiliatesApi.getMe()
      } catch (error) {
        if ((error as AxiosError).response?.status === 404) return null
        throw error
      }
    },
    {
      enabled: options?.enabled ?? true,
      initialData: options?.initialData,
      staleTime: options?.isLive ? LIVE_STALE_TIME_MS : CACHED_STALE_TIME_MS,
      refetchInterval: options?.isLive ? LIVE_REFRESH_INTERVAL_MS : false,
      refetchOnWindowFocus: Boolean(options?.isLive),
      retry: false,
    },
  )
